import React, { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import * as Icons from '@ant-design/icons';
import { queryClient } from '@/store/query';
import { Button, Col, message, Row, Checkbox, Form } from 'antd';

import { useConsolidatePDF } from '../../../hooks/useConsolidatePDF';
import { useDownloadPDF } from '../../../hooks/useDownloadPDF';
import { ROSA_QUERY_KEYS } from '../../../hooks/query-keys';
import { useRosaResultContext } from '../context';
import { Title } from '@/components/Typography';
import { PayloadDownloadPDF } from './types';
import { ModalContainer } from '../styles';
import { DEFAULT_RISK_RPN_COLOR, ACCEPTABLE_RISK_RPN_COLOR } from '@/utils/constants';

const { useForm } = Form;

interface DownloadPDFProps {
	hiddenConsolidate?: boolean;
}

export function DownloadPDF({ hiddenConsolidate }: DownloadPDFProps) {
	const [form] = useForm();

	const pdf = useDownloadPDF();
	const consolidate = useConsolidatePDF();

	const {
		company_id,
		organization_id,
		rosaResult: {
			id,
			consolidated,
			informations: { file_id }
		}
	} = useRosaResultContext();

	const [modalVisible, setModalVisible] = useState(false);

	const browserLanguage = window.navigator.language ?? 'en-US';

	const parameters: PayloadDownloadPDF = {
		id,
		file_id: file_id,
		company_id,
		organization_id,
		locale: browserLanguage
	};

	function onDownload(): void {
		const { pdf_parameters } = form.getFieldsValue();
		pdf.mutate(
			{ ...parameters, selected_pdf_sections: pdf_parameters },
			{
				onSuccess: (data: string) => {
					let archive = document.createElement('a');
					archive.target = '_blank';
					archive.href = data;
					archive.click();
					archive.remove();
				}
			}
		);
		closePDFModal();
	}

	function onConsolidate(payload: PayloadDownloadPDF): void {
		const alert_message = payload.status
			? 'Successfully consolidated analysis'
			: 'Successfully deconsolidated analysis';
		consolidate.mutate(payload, {
			onSuccess: () => {
				message.success(I18n.get(alert_message));
				queryClient.invalidateQueries([ROSA_QUERY_KEYS.GET_RESULT]);
				queryClient.invalidateQueries([ROSA_QUERY_KEYS.GET_ROSA]);
			}
		});
	}

	function openPDFModal(): void {
		setModalVisible(true);
	}

	function closePDFModal(): void {
		setModalVisible(false);
	}

	return (
		<>
			<Row justify={'end'}>
				<Col xl={6} xxl={4} style={{ display: 'flex', justifyContent: 'flex-end' }}>
					<Button
						style={{
							color: DEFAULT_RISK_RPN_COLOR,
							borderRadius: '0.25rem',
							border: `1px solid ${DEFAULT_RISK_RPN_COLOR}`,
							marginRight: !hiddenConsolidate ? '2rem' : '0'
						}}
						loading={pdf.isLoading}
						disabled={pdf.isLoading}
						onClick={() => openPDFModal()}
						icon={<Icons.DownloadOutlined style={{ color: DEFAULT_RISK_RPN_COLOR }} />}
					>
						{I18n.get('Download PDF')}
					</Button>
				</Col>
				{!hiddenConsolidate && (
					<Col xl={6} xxl={4} style={{ display: 'flex', justifyContent: 'flex-end' }}>
						{!consolidated ? (
							<Button
								style={{
									color: ACCEPTABLE_RISK_RPN_COLOR,
									borderRadius: '0.25rem',
									border: `1px solid ${ACCEPTABLE_RISK_RPN_COLOR}`
								}}
								loading={consolidate.isLoading}
								disabled={consolidate.isLoading}
								onClick={() => onConsolidate({ ...parameters, status: true })}
								icon={<Icons.LockOutlined style={{ color: ACCEPTABLE_RISK_RPN_COLOR }} />}
							>
								{I18n.get('Consolidate analysis')}
							</Button>
						) : (
							<Button
								style={{
									color: '#828282',
									borderRadius: '0.25rem',
									border: '1px solid #828282'
								}}
								loading={consolidate.isLoading}
								disabled={consolidate.isLoading}
								onClick={() => onConsolidate({ ...parameters, status: false })}
								icon={<Icons.UnlockOutlined style={{ color: '#828282' }} />}
							>
								{I18n.get('Deconsolidate')}
							</Button>
						)}
					</Col>
				)}
			</Row>
			<ModalContainer open={modalVisible} onCancel={() => closePDFModal()} width={500} footer={false} centered>
				<Row justify="center">
					<Col xs={24} style={{ textAlign: 'center' }}>
						<Title level={3}>PDF</Title>
						<p>{I18n.get('Select the items you want to present in your document')}</p>
					</Col>
					<Col xs={22}>
						<Form
							form={form}
							initialValues={{
								pdf_parameters: ['images', 'details', 'required_elements', 'action_plans']
							}}
						>
							<Row justify="center" gutter={[10, 0]} style={{ marginTop: '1rem', marginBottom: '1rem' }}>
								<Col xs={24}>
									<Form.Item name="pdf_parameters">
										<Checkbox.Group style={{ width: '100%' }}>
											<Row style={{ marginBottom: '1rem' }}>
												<Col xs={24}>
													<Checkbox value={'images'}>
														<strong style={{ fontSize: 16 }}>{I18n.get('Images')}</strong>
													</Checkbox>
												</Col>
											</Row>
											<Row style={{ marginBottom: '1rem' }}>
												<Col xs={24}>
													<Checkbox value={'details'} checked>
														<strong style={{ fontSize: 16 }}>
															{I18n.get('Filling details')}
														</strong>
													</Checkbox>
												</Col>
											</Row>
											<Row style={{ marginBottom: '1rem' }}>
												<Col xs={24}>
													<Checkbox value={'required_elements'} checked>
														<strong style={{ fontSize: 16 }}>
															{I18n.get('Required elements')}
														</strong>
													</Checkbox>
												</Col>
											</Row>
											<Row style={{ marginBottom: '1rem' }}>
												<Col xs={24}>
													<Checkbox value={'action_plans'} checked>
														<strong style={{ fontSize: 16 }}>
															{I18n.get('Action plan')}
														</strong>
													</Checkbox>
												</Col>
											</Row>
										</Checkbox.Group>
									</Form.Item>
								</Col>
							</Row>
							<Row justify="center">
								<Col span="auto" style={{ marginRight: '1rem' }}>
									<Button onClick={closePDFModal}>{I18n.get('Cancel')}</Button>
								</Col>
								<Col span="auto">
									<Button type="primary" onClick={onDownload}>
										{I18n.get('Generate document')}
									</Button>
								</Col>
							</Row>
						</Form>
					</Col>
				</Row>
			</ModalContainer>
		</>
	);
}
